"use client"

import React, { createContext, useContext, useState, useCallback } from "react"

export enum ToastType {
  Info = "info",
  Error = "error",
}

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number // Duration in milliseconds, defaults to 5000
}

interface ToastContextType {
  toasts: Toast[]
  showToast: (message: string, type?: ToastType, duration?: number) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const showToast = useCallback(
    (message: string, type: ToastType = ToastType.Info, duration?: number) => {
      const id = Math.random().toString(36).substring(2, 9)
      const toast: Toast = {
        id,
        message,
        type,
        duration: duration ?? 5000, // Default 5 seconds
      }

      setToasts((prev) => [...prev, toast])

      // Auto-dismiss after duration
      setTimeout(() => {
        removeToast(id)
      }, toast.duration)
    },
    [removeToast]
  )

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider")
  }
  return context
}
